import { useEffect } from 'react';
import { Modal, Stack, NumberInput, Textarea, Group, Button, Select } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { isoDate, fromIsoDate, apiError } from '../lib/journal';

/** Tahrirlanadigan baho — `PATCH /grades/:id` uchun kerak bo'ladigan minimal maydonlar. */
export interface EditableGrade {
  id: string;
  value: number;
  comment?: string | null;
  date: string;
  subjectId?: string | null;
}

interface Props {
  /** `null` — modal yopiq. */
  grade: EditableGrade | null;
  /** Fan almashtirishga ruxsat berilsa — ro'yxat. Berilmasa fan maydoni ko'rsatilmaydi
   *  (jurnal katagida fan ustun bilan qat'iy belgilangan). */
  subjects?: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Bahoni tahrirlash — kontrakt 2.2 dagi YANGI `PATCH /grades/:id`.
 * TEACHER faqat O'ZI qo'ygan bahoni tuzata oladi; bu cheklovni backend qo'yadi,
 * front esa tugmani ko'rsatmaslik bilan oldindan to'sadi (`canEditGrade`).
 */
export function GradeEditModal({ grade, subjects, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const form = useForm({
    initialValues: { value: 5, comment: '', date: null as Date | null, subjectId: '' },
    validate: { value: (v) => (v < 1 || v > 5 ? t('gradesPage.gradeRange') : null) },
  });

  // Modal ochilganda formani tanlangan baho bilan to'ldiramiz.
  // `form` qasddan bog'liqlikka qo'shilmadi: useForm har renderda yangi obyekt
  // qaytaradi va effekt cheksiz aylanardi.
  useEffect(() => {
    if (!grade) return;
    form.setValues({
      value: grade.value,
      comment: grade.comment ?? '',
      date: fromIsoDate(String(grade.date).slice(0, 10)),
      subjectId: grade.subjectId ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grade?.id]);

  const mut = useMutation({
    mutationFn: async (v: typeof form.values) => {
      if (!grade) return;
      await api.patch(`/grades/${grade.id}`, {
        value: v.value,
        comment: v.comment || null,
        date: isoDate(v.date),
        ...(subjects && v.subjectId ? { subjectId: v.subjectId } : {}),
      });
    },
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('gradesPage.editSaved') });
      onSaved();
      onClose();
    },
    onError: (e) => notifications.show({ color: 'red', message: apiError(e, t('common.error')) }),
  });

  return (
    <Modal opened={!!grade} onClose={onClose} title={t('gradesPage.edit')} centered>
      <form onSubmit={form.onSubmit((v) => mut.mutate(v))}>
        <Stack>
          <NumberInput label={t('gradesPage.gradeValue')} min={1} max={5} required {...form.getInputProps('value')} />
          {subjects && (
            <Select
              label={t('fields.subject')}
              data={subjects.map((s) => ({ value: s.id, label: s.name }))}
              searchable
              // Majburiy maydon: `clearable` yo'qligining O'ZI yetarli emas —
              // `allowDeselect` busiz tanlangan variantni qayta bosish bilan tozalanardi.
              allowDeselect={false}
              {...form.getInputProps('subjectId')}
            />
          )}
          <DateInput label={t('gradesPage.date')} valueFormat="DD.MM.YYYY" {...form.getInputProps('date')} />
          <Textarea label={t('gradesPage.comment')} autosize minRows={2} {...form.getInputProps('comment')} />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" loading={mut.isPending}>{t('common.save')}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
